<template>
    <div ref="calendar-wrapper" :style="styleOptions.container" class="calendar-container">
        <div :style="calendarWrapperStyles" class="calendar-wrapper">
            <div v-for="(month, indx) in calendarView" :key="indx" class="month">
                <p class="month-label">
                    <slot name="month-label" :month="month"></slot>
                </p>
                <div ref="calendar-month" :style="styleOptions.background" class="month-view">
                    <div class="header">
                        <span
                            v-for="content in calendarHeader"
                            :key="content"
                            :style="styleOptions.font"
                        >
                            {{ content }}
                        </span>
                    </div>
                    <div class="dates">
                        <div v-for="(day, indx) in month.days" :key="indx" class="date">
                            <span
                                v-if="day.day"
                                :class="dateClass(day)"
                                :style="styleOptions.font"
                                @click="emitClickAction(month, day)"
                                @mouseover="emitHoverAction(month, day)"
                                @mouseleave="emitUnHoverAction(month, day)"
                            >
                                {{ day.day }}
                                <slot name="day-content" :day="day"> </slot>
                            </span>
                            <span v-else> &nbsp; </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="paginate-actions">
            <slot
                name="paginate-actions"
                :handleNextMonths="handleNextMonths"
                :handlePrevMonths="handlePrevMonths"
            ></slot>
        </div>
    </div>
</template>
<script>
    /**
     * Base calendar component
     * Extendable by calendar view components
     *
     * extended components must implement:
     *   1. @property { object } computed - CSS classes determining how each month's view is aligned
     *   2. @property { function } methods - helper functions for computed CSS classes
     *
     *
     * @displayName BaseCalendar
     */

    import { MONTHS } from '@/constants/calendar'

    export default {
        name: 'ScrollableBaseCalendar',

        emits: ['dateClicked', 'dateHovered', 'dateUnhovered'],

        props: {
            /**
             * The number of months displayed as main
             */
            numOfViews: {
                type: Number,
                required: true,
                validator: (val) => val >= 0
            },

            /**
             * The number of months before and after current month will be part of slider
             */
            additionalMonths: {
                type: Number,
                required: true,
                validator: (val) => val >= 0
            },

            /**
             * Calendar from parent component to sync parent functionalities
             */
            calendar: {
                type: Array,
                required: true
            },

            /**
             * Custom calendar to render using custom calendar maker
             */
            customCalendar: {
                type: Object,
                required: false,
                default: () => ({}),
                validator: (v) => {
                    if (!Object.keys(v).length) return true

                    return 'maker' in v && 'today' in v
                }
            },

            /**
             * Custom calendar to render using custom calendar view
             */
            customCalendarView: {
                type: Array,
                required: false,
                default: () => [],
                validator: (v) => {
                    if (!v.length) return true

                    return v.every((o) => 'days' in o && 'label' in o)
                }
            },

            /**
             * Custom calendar styles
             */
            styleOptions: {
                type: Object,
                required: false,
                default: () => ({
                    background: {},
                    font: {} // NOTE: font-size above 30 is not recommended.
                })
            }
        },

        data: (self) => ({
            calendarView: self.calendar,
            viewIndx: null,
            currentMonth: null,
            calendarHeader: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
            currentMonthOffset: null,
            calendarWidth: null,
            wrapperWidth: null,
            wrapperGap: null
        }),

        methods: {
            calculateDimensions() {
                this.calculateWrapperGap()

                this.calendarWidth = this.$refs['calendar-month'][0].offsetWidth
                this.currentMonthOffset =
                    this.calendarWidth * this.additionalMonths +
                    (this.additionalMonths - 1.55) * this.wrapperGap

                this.calculateWrapperWidth()
            },
            calculateWrapperGap() {
                const firstMonth = this.$refs['calendar-month'][0]
                const secondMonth = this.$refs['calendar-month'][1]

                const firstMonthPos = firstMonth.offsetLeft + firstMonth.offsetWidth
                const secondMonthPos = secondMonth.offsetLeft

                const gap = secondMonthPos - firstMonthPos
                this.wrapperGap = gap
            },
            calculateWrapperWidth() {
                const hiddenShownPercentage = 0.11
                const hiddenWidth = hiddenShownPercentage * this.calendarWidth * 2

                this.wrapperWidth =
                    this.numOfViews * this.calendarWidth +
                    this.calendarWidth * (this.additionalMonths * 2) +
                    hiddenWidth +
                    this.wrapperGap * (this.additionalMonths * 2)
            },
            emitClickAction(month, day) {
                if (!day.today && !day.past) {
                    this.$emit('dateClicked', month.label, day)
                }
            },

            emitHoverAction(month, day) {
                this.$emit('dateHovered', month.label, day)
            },

            emitUnHoverAction(month, day) {
                this.$emit('dateUnhovered', month.label, day)
            },

            handlePrevMonths() {
                if (this.viewIndx <= 0) {
                    this.viewIndx = this.calendar.length - 1
                } else {
                    this.viewIndx = this.viewIndx - 1
                }
            },

            handleNextMonths() {
                if (this.viewIndx >= this.calendar.length - 1) {
                    this.viewIndx = 0
                } else {
                    this.viewIndx = this.viewIndx + 1
                }
            },

            getMonthLabel(label) {
                return `${MONTHS[label[0]]} ${label[1]}`
            },

            getCalendarDays(month, currentDay) {
                let days = []

                for (let i = 0; i < month.firstDay; i++) {
                    days.push({
                        day: null
                    })
                }

                for (let i = 0; i < month.totalDays; i++) {
                    let past

                    if (this.customCalendar.today) {
                        past = this.markPrevDates(this.customCalendar.today, i + 1)
                    } else {
                        past = this.checkForPrevDates(month, i + 1)
                    }

                    days.push({
                        past: past,
                        day: i + 1,
                        current: currentDay === i + 1,
                        today: currentDay === i + 1 && this.isToday(month.label, i + 1)
                    })
                }

                return days
            },

            isToday(label, day) {
                let today = new Date()
                let year = parseInt(label.split(' ')[1])
                let month = parseInt(label.split(' ')[0])
                return (
                    today.getFullYear() === year &&
                    today.getMonth() === month &&
                    today.getDate() === day
                )
            },

            markPrevDates(today, day) {
                return today >= day
            },

            checkForPrevDates(monthObj, day) {
                let today = new Date()
                today = new Date(today.valueOf())
                today.setDate(today.getDate() - 1)

                let year = monthObj.label.split(' ')[1]
                let month = monthObj.label.split(' ')[0]
                let date = new Date(year, month, day)

                return today >= date
            },

            currentMonthMaker(calendarMaker, today) {
                let firstDay = new Date(today.getFullYear(), today.getMonth(), 1).getDay()
                let totalDays = new Date(
                    today.getFullYear(),
                    today.getMonth() + 1,
                    0
                ).getDate()
                let label = `${today.getMonth()} ${today.getFullYear()}`
                let currentMonth = {
                    label,
                    firstDay,
                    totalDays,
                    currentDay: today.getDate()
                }
                calendarMaker.push(currentMonth)

                this.currentMonth = currentMonth

                return calendarMaker
            },

            futureMonthsMaker(calendarMaker, today) {
                for (let i = 0; i < this.additionalMonths; i++) {
                    today = new Date(today.getFullYear(), today.getMonth() + 1, 1)

                    let firstDay = new Date(today.getFullYear(), today.getMonth(), 1).getDay()
                    let totalDays = new Date(
                        today.getFullYear(),
                        today.getMonth() + 1,
                        0
                    ).getDate()
                    let label = `${today.getMonth()} ${today.getFullYear()}`

                    calendarMaker.push({
                        label,
                        firstDay,
                        totalDays
                    })
                }

                return calendarMaker
            },

            pastMonthsMaker(calendarMaker, today) {
                for (let i = this.additionalMonths; i > 0; i--) {
                    today = new Date(today.getFullYear(), today.getMonth() - 1, 1)

                    let firstDay = new Date(today.getFullYear(), today.getMonth(), 1).getDay()
                    let totalDays = new Date(
                        today.getFullYear(),
                        today.getMonth() + 1,
                        0
                    ).getDate()
                    let label = `${today.getMonth()} ${today.getFullYear()}`

                    calendarMaker.unshift({
                        label,
                        firstDay,
                        totalDays
                    })
                }

                return calendarMaker
            },

            createCalendarMaker() {
                let calendarMaker = []
                let today = new Date()

                let currentMonth = this.currentMonthMaker([], today)[0]
                calendarMaker.push(currentMonth)
                calendarMaker = this.futureMonthsMaker(calendarMaker, today)
                calendarMaker = this.pastMonthsMaker(calendarMaker, today)

                this.viewIndx = calendarMaker.indexOf(this.currentMonth)

                return calendarMaker
            },

            buildCalendar(calendarMaker) {
                let calendar = []

                for (let i = 0; i < calendarMaker.length; i++) {
                    calendar.push({
                        label: this.getMonthLabel(calendarMaker[i].label.split(' ')),
                        days: this.getCalendarDays(
                            calendarMaker[i],
                            calendarMaker[i].currentDay
                        )
                    })
                }

                return calendar
            },

            initCalendar() {
                let calendarMaker

                if (!Object.keys(this.customCalendar).length) {
                    calendarMaker = this.createCalendarMaker()
                } else {
                    calendarMaker = this.customCalendar.maker
                }

                if (!this.customCalendarView.length) {
                    this.calendarView = this.buildCalendar(calendarMaker)
                } else {
                    this.calendarView = this.customCalendarView
                }
                this.$emit('update:calendar', this.calendarView)
            }
        },

        computed: {
            /**
             * Please implement new date classes here,
             * as needed by parent component's functionalities
             * and corresponding styles below
             */
            calendarWrapperStyles: (self) => {
                if (self.wrapperWidth) {
                    return {
                        ...self.styleOptions.wrapper,
                        width: `${self.wrapperWidth}px`
                    }
                }
                return {
                    ...self.styleOptions.wrapper
                }
            },
            dateClass: () => (day) => {
                let _class = {}

                if (day.past) {
                    _class['past-dates'] = true
                }

                // ThreeMonthsView Styles
                if (day.unavailable) {
                    _class['unavailable-date'] = true
                }
                if (day.selected) {
                    _class['selected-date'] = true
                }
                if (day.select) {
                    _class['select-date'] = true
                    _class['unavailable-date'] = false
                }
                if (day.highlighted) {
                    _class['highlighted-dates'] = true
                }
                if (day.hover) {
                    _class['hovered-date'] = true
                    _class['select-date'] = false
                }
                if (day.unselect) {
                    _class['unselect-date'] = true
                    _class['select-date'] = false
                }

                // MarketingCalendar styles
                if (day.current) {
                    _class['current-date'] = true
                }
                if (day.animate && day.color) {
                    _class[`${day.color}-date`] = true
                }
                if (day.hover) {
                    _class['hovered-date'] = true
                }
                if (day.leaveAnimation) {
                    _class['leave-animated-date'] = true
                }

                // ArtistCalendar styles
                if (day.current) {
                    _class['current-date'] = true
                }
                if (day.notAvail) {
                    _class['not-avail'] = true
                }
                if (day.booked) {
                    _class['booked-date'] = true
                }
                if (day.notAvail && day.booked) {
                    _class['not-avail'] = false
                }

                // Booking styles
                if (day.hover) {
                    _class['hovered-date'] = true
                }
                if (day.past) {
                    _class['past-dates'] = true
                    _class['hovered-date'] = false
                }
                if (day.availsChecked || day.invited) {
                    _class['avails-checked'] = true
                    _class['hovered-date'] = false
                }
                if (day.book || day.offerMade) {
                    _class['book'] = true
                    _class['hovered-date'] = false
                }
                if (day.advance) {
                    _class['advance'] = true
                    _class['hovered-date'] = false
                }
                if (day.wrapUp) {
                    _class['wrap-up'] = true
                    _class['hovered-date'] = false
                }
                if (day.cancelled) {
                    _class['cancelled'] = true
                    _class['hovered-date'] = false
                }
                if (day.completed) {
                    _class['completed'] = true
                    _class['hovered-date'] = false
                }
                if (day.today) {
                    _class['today'] = true
                    _class['hovered-date'] = false
                    _class['avails-checked'] = false
                    _class['book'] = false
                    _class['advance'] = false
                    _class['wrap-up'] = false
                    _class['cancelled'] = false
                    _class['completed'] = false
                }
                if (day.artistNotAvail) {
                    _class['artist-not-avail'] = true
                    _class['today'] = false
                    _class['hovered-date'] = false
                    _class['avails-checked'] = false
                    _class['book'] = false
                    _class['advance'] = false
                    _class['wrap-up'] = false
                    _class['cancelled'] = false
                    _class['completed'] = false
                }

                return _class
            }
        },

        created() {
            this.initCalendar()
        },

        mounted() {
            const calendar = this.$refs['calendar-wrapper']

            this.calculateDimensions()

            this.$nextTick(() => {
                calendar.scrollTo({
                    left: this.currentMonthOffset,
                    behavior: 'smooth'
                })
            })
        }
    }
</script>
<style lang="scss">
    @keyframes red-date-enter {
        0% {
            color: #d47ebf;
            opacity: 0;
            border: none;
        }
        25% {
            color: #d47ebf;
            opacity: 0.4;
            border: none;
        }
        50% {
            color: #d47ebf;
            opacity: 0.6;
            border: 1.5px solid #d47ebf;
        }
        75% {
            color: #d47ebf;
            opacity: 0.8;
            border: 1.5px solid #d47ebf;
        }
        100% {
            color: #d47ebf;
            opacity: 1;
            border: 1.5px solid #d47ebf;
        }
    }
    @keyframes orange-date-enter {
        0% {
            color: #cb9b1f;
            opacity: 0;
            border: none;
        }
        25% {
            color: #cb9b1f;
            opacity: 0.4;
            border: none;
        }
        50% {
            color: #cb9b1f;
            opacity: 0.6;
            border: 1.5px solid #cb9b1f;
        }
        75% {
            color: #cb9b1f;
            opacity: 0.8;
            border: 1.5px solid #cb9b1f;
        }
        100% {
            color: #cb9b1f;
            opacity: 1;
            border: 1.5px solid #cb9b1f;
        }
    }
    @keyframes yellow-date-enter {
        0% {
            color: #c9cd00;
            opacity: 0;
            border: none;
        }
        25% {
            color: #c9cd00;
            opacity: 0.4;
            border: none;
        }
        50% {
            color: #c9cd00;
            opacity: 0.6;
            border: 1.5px solid #c9cd00;
        }
        75% {
            color: #c9cd00;
            opacity: 0.8;
            border: 1.5px solid #c9cd00;
        }
        100% {
            color: #c9cd00;
            opacity: 1;
            border: 1.5px solid #c9cd00;
        }
    }
    @keyframes animated-date-leave {
        0% {
            color: #dedede;
            border: none;
            transform: translate(0%, 0%);
        }
        25% {
            color: #dedede;
            border: none;
            transform: translate(-3%, -3%);
        }
        50% {
            color: #dedede;
            border: none;
            transform: translate(-6%, -6%);
        }
        75% {
            color: #dedede;
            border: none;
            transform: translate(-3%, -3%);
        }
        100% {
            color: #dedede;
            border: none;
            transform: translate(0%, 0%);
        }
    }
    .calendar-container::-webkit-scrollbar {
        display: none;
    }
    .calendar-container {
        overflow-x: scroll;
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */

        .calendar-wrapper {
            display: flex;
            flex-direction: row;
            gap: 11.4px;

            .month {
                display: flex;
                flex-direction: column;
                min-height: 290px;

                .month-label {
                    color: #dedede;
                    font-family: Instrument Sans;
                    font-size: 22px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 120%;
                    letter-spacing: -0.22px;
                    margin-bottom: 12px;
                }

                .month-view {
                    width: auto;
                    height: auto;
                    background: #464646;
                    padding: 15px 25px 15px 25px;
                    border-radius: 15px;

                    .header {
                        display: flex;
                        flex-direction: row;
                        justify-content: space-between;
                        gap: 11.63px;
                    }

                    .dates {
                        display: flex;
                        flex-direction: row;
                        gap: calc((100% - 224px) / 6);
                        flex-wrap: wrap;

                        .date {
                            position: relative;
                            padding-top: calc((100% - 224px) / 6);
                        }
                    }

                    span {
                        cursor: grab;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 32px;
                        height: 32px;
                        border-radius: 100px;
                        color: #dedede;
                        text-align: center;
                        font-family: Instrument Sans;
                        font-size: 14px;
                        font-style: normal;
                        font-weight: 700;
                        line-height: 130%;
                        letter-spacing: 0.14px;
                    }

                    .past-dates {
                        color: #646464;
                    }

                    .selected-date,
                    .selected-date.highlighted-dates {
                        color: black;
                        background: #cdeb09;
                        border-radius: 100px;
                    }

                    .select-date {
                        color: #cdeb09;
                        border-radius: 100px;
                        border: 1.5px solid #cdeb09;
                    }

                    .unavailable-date {
                        border: 1.5px solid #646464;
                        color: #646464 !important;
                    }

                    .highlighted-dates {
                        color: #cdeb09;
                    }

                    .current-date {
                        color: #cdeb09;
                    }

                    .red-date {
                        animation: red-date-enter 0.15s;
                        animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
                        color: #d47ebf;
                        border: 1.5px solid #d47ebf;
                    }
                    .orange-date {
                        animation: orange-date-enter 0.15s;
                        animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
                        color: #cb9b1f;
                        border: 1.5px solid #cb9b1f;
                    }
                    .yellow-date {
                        animation: yellow-date-enter 0.15s;
                        animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
                        color: #c9cd00;
                        border: 1.5px solid #c9cd00;
                    }
                    .leave-animated-date {
                        animation: animated-date-leave 0.15s;
                        animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
                        color: #dedede;
                        border: none;
                    }

                    .hovered-date:hover {
                        background: url(/images/icons/calendar/hoveredSelect.svg);
                        color: transparent;
                    }
                    .unselect-date:hover {
                        background: url(/images/icons/calendar/hoveredUnselect.svg);
                        color: transparent;
                    }
                    .not-avail {
                        background: url(/images/icons/booked-white.svg) no-repeat;
                        background-size: 30px 30px;
                        background-position: 1px 1px;
                    }
                    .artist-not-avail {
                        color: #646464;
                    }
                    .booked-date {
                        color: #c9cd00;
                        border: 1.5px solid #c9cd00;
                    }

                    .today {
                        color: #cdeb09;
                    }
                    .avails-checked {
                        color: #cdeb09;
                    }
                    .book {
                        color: #c9cd00;
                    }
                    .advance {
                        color: #cb9b1f;
                    }
                    .wrap-up {
                        color: #d188bf;
                    }
                    .completed {
                        color: #646464;
                    }
                    .cancelled {
                        color: #ba87ff;
                        background: url(/images/icons/booked-purple.svg) no-repeat;
                        background-size: 30px 30px;
                        background-position: 1px 1px;
                    }
                }
            }
        }
    }
</style>
